export default class QboxmailApi {
  constructor(opts = {}) {
    this.baseURL = opts.baseURL || BASE_URL;
    this.useCookie = opts.useCookie || false;
    this.returnRaw = opts.returnRaw;
    this.signal = opts.signal;

    if (!this.useCookie) {
      this.token = opts.token;
    }

    if (!this.useCookie && !this.token) {
      throw new Error("valid API token is missing");
    }
  }

  generateSWRFetcher() {
    return (url) =>
      this.request(`${this.baseURL}${url}`, {
        method: "GET",
      });
  }

  stringifyQueryParams(params = {}) {
    // clean falsey values
    Object.keys(params).forEach(function (k) {
      if (!params[k]) {
        delete params[k];
      }
    });
    return Object.keys(params)
      .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
      .join("&");
  }

  // request action
  request(url, opts = {}) {
    const headers = new Headers({
      "Content-Type": "application/json",
      Accept: "application/json",
    });

    // const { signal, ...restOpts } = opts;

    const defaults = {
      credentials: this.useCookie ? "include" : "omit",
      mode: "cors",
      headers,
    };

    const options = {
      ...defaults,
      ...opts,
      ...(this.signal ? { signal: this.signal } : {}),
    };

    return fetch(url, options).then((response) => {
      if (this.returnRaw) {
        console.log("returning raw response");
        return response;
      }

      // it is 2xx
      if (response.ok) {
        if (response.status === 204) {
          return response;
        }

        return response.json().then((data) => data);
      } else {
        return Promise.reject(response);
      }
    });
  }
  // aliases
  get(url, queryParams, opts) {
    const queryString = typeof queryParams === "string" ? queryParams : `?${this.stringifyQueryParams(queryParams)}`;
    return this.request(`${url}${queryString}`, {
      method: "GET",
      ...opts,
    });
  }

  post(url, opts) {
    return this.request(url, { method: "POST", ...opts });
  }

  put(url, opts) {
    return this.request(url, { method: "PUT", ...opts });
  }

  delete(url, opts) {
    return this.request(url, { method: "DELETE", ...opts });
  }
  // authentication actions
  login() {}

  logout() {}

  //otp methods

  generateOTPSecret(userCode) {
    return this.post(`${this.baseURL}/users/${userCode}/otp`);
  }

  verifyOTP(userCode, otp, action) {
    return this.post(`${this.baseURL}/users/${userCode}/otp/verify/status`, {
      body: JSON.stringify({
        otp,
        status: action,
      }),
    });
  }

  deleteSession(userCode, id) {
    return this.delete(`${this.baseURL}/users/${userCode}/otp/session_token/${id}`);
  }

  // GET actions
  validateDomainName(domainName) {
    return this.get(`${this.baseURL}/validate/domain_name/`, {
      domain_name: domainName,
    });
  }

  validateDeliveryDomainName(domainName) {
    return this.get(`${this.baseURL}/validate/delivery/domain_name/`, {
      domain_name: domainName,
    });
  }

  validateAliasDomainName(aliasDomainName) {
    return this.get(`${this.baseURL}/validate/alias_domain_name/`, {
      alias_domain_name: aliasDomainName,
    });
  }

  validateEmailName(domainCode, emailName) {
    return this.get(`${this.baseURL}/validate/email_name/`, {
      email_name: emailName,
      domain_code: domainCode,
    });
  }

  validateArchiveLimits(domainCode, emailCodes) {
    return this.put(`${this.baseURL}/validate/archive_limits`, {
      body: JSON.stringify({
        domain_code: domainCode,
        email_codes: emailCodes,
      }),
    });
  }

  validateDelegateEmail(email_address) {
    return this.get(`${this.baseURL}/validate/delegate_email_address`, {
      email_address,
    });
  }

  validateTeamMemberEmail(email_address) {
    return this.get(`${this.baseURL}/validate/team_member_email_address`, {
      email_address,
    });
  }

  validateAliasEmailName(domainCode, aliasEmailName) {
    return this.get(`${this.baseURL}/validate/alias_email_name/`, {
      alias_email_name: aliasEmailName,
      domain_code: domainCode,
    });
  }

  generateWHMCSUrl(action, order_id = null) {
    return this.post(`${this.baseURL}/orders/whmcs_url`, {
      body: JSON.stringify({ action, order_id }),
    });
  }

  refreshAccount() {
    return this.get(`${this.baseURL}/users/session`);
  }

  refreshAdmins(queryParams) {
    return this.get(`${this.baseURL}/users/admins`, queryParams);
  }

  refreshDomains(queryParams) {
    return this.get(`${this.baseURL}/domains`, queryParams);
  }

  refreshArchiveDomains(queryParams) {
    return this.get(`${this.baseURL}/archive/domains`, queryParams);
  }

  refreshDelegates(queryParams) {
    return this.get(`${this.baseURL}/delegates`, queryParams);
  }

  refreshDelegate(code) {
    return this.get(`${this.baseURL}/delegates/${code}`);
  }

  refreshEmailAccounts(codeDomain, queryParams = {}) {
    return this.get(`${this.baseURL}/domains/${codeDomain}/email_accounts`, queryParams);
  }

  refreshArchiveEmailAccounts(domainCode, queryParams = {}) {
    return this.get(`${this.baseURL}/archive/domains/${domainCode}/email_accounts`, queryParams);
  }

  refreshEmailAccount(codeDomain, codeEmail, queryParams = {}) {
    return this.get(`${this.baseURL}/domains/${codeDomain}/email_accounts/${codeEmail}`, queryParams);
  }

  refreshTeamMembers(queryParams) {
    return this.get(`${this.baseURL}/team_members`, queryParams);
  }

  refreshManagers(queryParams) {
    return this.get(`${this.baseURL}/managers`, queryParams);
  }

  refreshTeamMember(code) {
    return this.get(`${this.baseURL}/team_members/${code}`);
  }

  refreshManager(code) {
    return this.get(`${this.baseURL}/managers/${code}`);
  }

  refreshCustomers(queryParams) {
    return this.get(`${this.baseURL}/users/customers`, queryParams);
  }

  refreshCustomer(code) {
    return this.get(`${this.baseURL}/users/customers/${code}`);
  }

  refreshImports(queryParams) {
    return this.get(`${this.baseURL}/imports`, queryParams);
  }

  refreshMigrations(queryParams) {
    return this.get(`${this.baseURL}/migrations`, queryParams);
  }

  refreshMigrationOptions(queryParams) {
    return this.get(`${this.baseURL}/migration_options`, queryParams);
  }

  refreshDomain(code) {
    return this.get(`${this.baseURL}/domains/${code}`);
  }

  refreshDomainsEmailAccounts(domainCode, queryParams) {
    return this.get(`${this.baseURL}/domains/${domainCode}/email_accounts/`, queryParams);
  }

  refreshDomainsAliasDomains(domainCode, queryParams) {
    return this.get(`${this.baseURL}/domains/${domainCode}/aliases`, queryParams);
  }

  refreshDomainsAliasEmailAccounts(domainCode, queryParams) {
    return this.get(`${this.baseURL}/domains/${domainCode}/alias_email_accounts/`, queryParams);
  }

  refreshCustomizations(queryParams) {
    return this.get(`${this.baseURL}/customizations`, queryParams);
  }

  refreshMainCustomization(queryParams) {
    return this.get(`${this.baseURL}/customizations/main`, queryParams);
  }

  refreshAliasDomain(domainCode, code) {
    return this.get(`${this.baseURL}/domains/${domainCode}/aliases/${code}`);
  }

  getEmailSignatures(email) {
    return this.get(`${this.baseURL}/signatures/${email}`);
  }

  getDomains(queryParams) {
    return this.get(`${this.baseURL}/domains`, queryParams);
  }

  getPostmasters(queryParams) {
    return this.get(`${this.baseURL}/domains/postmasters`, queryParams);
  }

  getDomainsTotals() {
    return this.get(`${this.baseURL}/domains/totals`);
  }

  getArchiveDomains(queryParams) {
    return this.get(`${this.baseURL}/archive/domains`, queryParams);
  }

  getArchiveEmailAccounts(domainCode, queryParams) {
    return this.get(`${this.baseURL}/archive/domains/${domainCode}/email_accounts`, queryParams);
  }

  getTeamMembers(queryParams) {
    return this.get(`${this.baseURL}/team_members`, queryParams);
  }

  getPermissions() {
    return this.get(`${this.baseURL}/team_members/perms`);
  }

  getManagers(queryParams) {
    return this.get(`${this.baseURL}/managers`, queryParams);
  }

  getCustomers(queryParams) {
    return this.get(`${this.baseURL}/users/customers`, queryParams);
  }

  getCharts(queryParams) {
    return this.get(`${this.baseURL}/dashboard/charts`, queryParams);
  }

  getSystemVersion() {
    return this.get(`${this.baseURL}/system/version`);
  }

  getAdmins(queryParams) {
    return this.get(`${this.baseURL}/users/admins`, queryParams);
  }

  getAuditLogs(queryParams) {
    return this.get(`${this.baseURL}/audits`, queryParams);
  }

  getAuditLogsPagination(queryParams) {
    return this.get(`${this.baseURL}/audits/pagination`, queryParams);
  }

  getAuditFormInit() {
    return this.get(`${this.baseURL}/audits/form_init`);
  }

  getDomain(code) {
    return this.get(`${this.baseURL}/domains/${code}`);
  }

  getDomainSignatureTemplates(code) {
    return this.get(`${this.baseURL}/domains/${code}/settings/signature_templates`);
  }

  getEmailsWithoutSignature(code) {
    return this.get(`${this.baseURL}/domains/${code}/settings/no_signature`);
  }

  getTeamMember(code) {
    return this.get(`${this.baseURL}/team_members/${code}`);
  }

  getManager(code) {
    return this.get(`${this.baseURL}/managers/${code}`);
  }

  getCustomer(code) {
    return this.get(`${this.baseURL}/users/customers/${code}`);
  }

  getAdmin(code) {
    return this.get(`${this.baseURL}/users/admins/${code}`);
  }

  getDomainsEmailAccounts(domainCode, queryParams) {
    return this.get(`${this.baseURL}/domains/${domainCode}/email_accounts`, queryParams);
  }

  getDomainsAliasDomains(domainCode, queryParams) {
    return this.get(`${this.baseURL}/domains/${domainCode}/aliases`, queryParams);
  }

  getDomainsAliasEmailAccounts(domainCode, queryParams) {
    return this.get(`${this.baseURL}/domains/${domainCode}/alias_email_accounts/`, queryParams);
  }

  getEmailAccount(domainCode, emailCode) {
    return this.get(`${this.baseURL}/domains/${domainCode}/email_accounts/${emailCode}`);
  }

  getAliasDomain(domainCode, aliasDomainCode) {
    return this.get(`${this.baseURL}/domains/${domainCode}/aliases/${aliasDomainCode}`);
  }

  getAliasEmailAccount(domainCode, aliasEmailAccountCode) {
    return this.get(`${this.baseURL}/domains/${domainCode}/alias_email_accounts/${aliasEmailAccountCode}`);
  }

  getImports(queryParams) {
    return this.get(`${this.baseURL}/imports`, queryParams);
  }

  getArchiveImportFolders(code) {
    return this.get(`${this.baseURL}/vault_imports/folders`, {
      email_code: code,
    });
  }

  getImportResources(code, queryParams) {
    return this.get(`${this.baseURL}/imports/${code}/resources`, queryParams);
  }

  getMigrationResources(code, queryParams) {
    return this.get(`${this.baseURL}/migrations/${code}/resources`, queryParams);
  }

  getMigration(code) {
    return this.get(`${this.baseURL}/migrations/${code}`);
  }

  getMigrationPresets() {
    return this.get(`${this.baseURL}/migrations/presets`);
  }

  getMigrationStatus(query) {
    return this.get(`${this.baseURL}/migrations/status`, query);
  }

  getMigrationRun(migrationCode, code) {
    return this.get(`${this.baseURL}/migrations/${migrationCode}/runs/${code}`);
  }

  getQbmoveLog(code) {
    return this.get(`${this.baseURL}/migrations/log/${code}`);
  }

  refreshMigrationResources(code, queryParams) {
    return this.get(`${this.baseURL}/migrations/${code}/resources`, queryParams);
  }

  // getMigration(code) {
  //   return this.get(`${this.baseURL}/migrations/${code}`);
  // }

  getMigrationOptions(queryParams) {
    return this.get(`${this.baseURL}/migration_options`, queryParams);
  }

  getMigrationResourceLog(migrationCode, resourceCode, logNo) {
    return this.get(`${this.baseURL}/migrations/${migrationCode}/resources/${resourceCode}`, {
      log_no: logNo,
    });
  }

  getMigrations(queryParams) {
    return this.get(`${this.baseURL}/migrations`, queryParams);
  }

  getCustomizations(queryParams) {
    return this.get(`${this.baseURL}/customizations`, queryParams);
  }

  getManagersCustomizations(queryParams = {}) {
    return this.get(`${this.baseURL}/customizations/managers`, queryParams);
  }

  getCustomization(id) {
    return this.get(`${this.baseURL}/customizations/${id}`);
  }

  getMainCustomization(queryParams = {}) {
    return this.get(`${this.baseURL}/customizations/main`, queryParams);
  }

  getNews(queryParams) {
    return this.get(`${this.baseURL}/news`, queryParams);
  }

  getSingleNews(id) {
    return this.get(`${this.baseURL}/news/${id}`);
  }

  getAllDomainsWithoutCustomizations(params = {}) {
    return this.get(`${this.baseURL}/domains_no_customizations`, {
      per: 20000,
      ...params,
    });
  }

  getAllDomainsWithoutManagers(params = {}) {
    return this.get(`${this.baseURL}/domains_no_managers`, {
      per: 20000,
      ...params,
    });
  }

  getOutboundLimits(code) {
    return this.get(`${this.baseURL}/domains/${code}/outbound_limits`);
  }

  generateReport(type, customerCode = null) {
    return this.get(`${this.baseURL}/reports/${type}`, {
      customer_code: customerCode,
    });
  }

  getEmailOutboundLimits(codeDomain, codeEmail) {
    return this.get(`${this.baseURL}/domains/${codeDomain}/email_accounts/${codeEmail}/outbound_limits`);
  }

  getAuditsActors(filter) {
    return this.get(`${this.baseURL}/audits/actor_filters?filter=${filter}`);
  }

  getDelegates(queryParams) {
    return this.get(`${this.baseURL}/delegates`, queryParams);
  }

  getDelegate(code) {
    return this.get(`${this.baseURL}/delegates/${code}`);
  }

  getAuditsResources(type, query) {
    return this.get(`${this.baseURL}/audits/resources`, { type, query });
  }

  getDeliveryDashboard() {
    return this.get(`${this.baseURL}/delivery/dashboard`);
  }

  getDeliveryDomain(domainCode) {
    return this.get(`${this.baseURL}/delivery/domains/${domainCode}`);
  }

  getDeliveryDomains = (params = {}) => {
    return this.get(`${this.baseURL}/delivery/domains`, params);
  };

  getDeliveryAccounts(params = {}) {
    return this.get(`${this.baseURL}/delivery/accounts`, params);
  }

  getDeliveryReputationChanges(params = {}) {
    return this.get(`${this.baseURL}/delivery/reputation/changes`, params);
  }

  getDeliveryResourcesWithoutManagers(params = {}) {
    return this.get(`${this.baseURL}/delivery/resources_no_managers`, params);
  }

  getSuppressionList() {
    return this.get(`${this.baseURL}/delivery/suppression_lists`);
  }

  getSuppressionListItem({ email, customer_code }) {
    return this.post(`${this.baseURL}/delivery/suppression_lists/details`, {
      body: JSON.stringify({ email, customer_code }),
    });
  }

  upgradeAnnuallyProduct(values) {
    return this.post(`${this.baseURL}/orders/upgrade`, {
      body: JSON.stringify(values),
    });
  }

  applySignatureTemplate(code, values) {
    return this.post(`${this.baseURL}/domains/${code}/settings/apply_signature_templates`, {
      body: JSON.stringify(values),
    });
  }

  // POST actions
  enableDKIM(code) {
    return this.post(`${this.baseURL}/domains/${code}/dkim`);
  }

  createEmailSignature(code, body) {
    return this.post(`${this.baseURL}/signatures/${code}`, {
      body: JSON.stringify(body),
    });
  }

  createDomain(values) {
    return this.post(`${this.baseURL}/domains`, {
      body: JSON.stringify(values),
    });
  }

  createEmailAccount(domainCode, values) {
    return this.post(`${this.baseURL}/domains/${domainCode}/email_accounts`, {
      body: JSON.stringify(values),
    });
  }

  enableArchive(domainCode, values) {
    return this.post(`${this.baseURL}/archive/domains/${domainCode}`, {
      body: JSON.stringify(values),
    });
  }

  enableArchiveEmail(domainCode, values) {
    return this.post(`${this.baseURL}/archive/domains/${domainCode}/email_accounts`, {
      body: JSON.stringify(values),
    });
  }

  createAliasEmail(domainCode, values) {
    return this.post(`${this.baseURL}/domains/${domainCode}/alias_email_accounts`, {
      body: JSON.stringify(values),
    });
  }

  createAliasDomain(domainCode, values) {
    return this.post(`${this.baseURL}/domains/${domainCode}/aliases`, {
      body: JSON.stringify(values),
    });
  }

  createImport(values) {
    return this.post(`${this.baseURL}/imports`, {
      body: JSON.stringify(values),
    });
  }

  createMigration(values) {
    return this.post(`${this.baseURL}/migrations`, {
      body: JSON.stringify(values),
    });
  }

  createCustomization(values, mainUrl) {
    return this.post(`${this.baseURL}/customizations${mainUrl}`, {
      body: JSON.stringify(values),
    });
  }

  createAdmin(values) {
    return this.post(`${this.baseURL}/users/admins`, {
      body: JSON.stringify(values),
    });
  }

  createCustomer(values) {
    return this.post(`${this.baseURL}/users/customers`, {
      body: JSON.stringify(values),
    });
  }

  createMigrationOption(values) {
    return this.post(`${this.baseURL}/migration_options`, {
      body: JSON.stringify(values),
    });
  }

  createBillingInfo(values) {
    return this.post(`${this.baseURL}/orders/billing_info`, {
      body: JSON.stringify(values),
    });
  }

  createOrder(values) {
    return this.post(`${this.baseURL}/orders`, {
      body: JSON.stringify(values),
    });
  }

  addDomainsToOrder(values) {
    return this.post(`${this.baseURL}/orders/domains`, {
      body: JSON.stringify(values),
    });
  }

  createManager(values) {
    return this.post(`${this.baseURL}/managers`, {
      body: JSON.stringify(values),
    });
  }

  createTeamMember(values) {
    return this.post(`${this.baseURL}/team_members`, {
      body: JSON.stringify(values),
    });
  }

  createNews(values) {
    return this.post(`${this.baseURL}/news`, {
      body: JSON.stringify(values),
    });
  }

  createTicket(values) {
    return this.post(`${this.baseURL}/support`, {
      body: JSON.stringify(values),
    });
  }

  createArchiveImport(code, values) {
    return this.post(`${this.baseURL}/vault_imports/${code}`, {
      body: JSON.stringify({ mappings: values }),
    });
  }

  createDelegate(values) {
    return this.post(`${this.baseURL}/delegates`, {
      body: JSON.stringify(values),
    });
  }

  createDelegatePasswordResetRequest(code) {
    return this.post(`${this.baseURL}/users/reset_password`, {
      body: JSON.stringify({ identifier: code }),
    });
  }

  createDeliveryDomain(params) {
    return this.post(`${this.baseURL}/delivery/domains`, {
      body: JSON.stringify(params),
    });
  }

  validateDeliveryDomain(code) {
    return this.post(`${this.baseURL}/delivery/domains/${code}/verification`);
  }

  skipValidateDeliveryDomain(code) {
    return this.post(`${this.baseURL}/delivery/domains/${code}/skip_verification`);
  }

  createDeliveryAccount(params) {
    return this.post(`${this.baseURL}/delivery/accounts`, {
      body: JSON.stringify(params),
    });
  }

  exportTracemailDeliveryPDF(log) {
    return this.post(`${this.baseURL}/delivery/tracemail/export`, {
      body: JSON.stringify({
        log: log,
      }),
    });
  }

  addToSuppressionList({ email, reason, accounts, customer_code }) {
    const body = { email, reason, ...(accounts && { accounts }), ...(customer_code && { customer_code }) };

    return this.post(`${this.baseURL}/delivery/suppression_lists`, {
      body: JSON.stringify(body),
    });
  }

  exportSuppressionList(account) {
    return this.post(`${this.baseURL}/delivery/suppression_lists/export`, {
      body: JSON.stringify({ account }),
    });
  }

  createImportSuppressionList(data) {
    return this.post(`${this.baseURL}/delivery/suppression_lists/import`, {
      body: JSON.stringify(data),
    });
  }

  addToDeliveryWaitlist(data) {
    return this.post(`${this.baseURL}/delivery/waitlist`, {
      body: JSON.stringify(data),
    });
  }

  letInDeliveryWaitlist(data) {
    return this.post(`${this.baseURL}/delivery/waitlist/accept`, {
      body: JSON.stringify(data),
    });
  }

  shareDeliveryDomainConfiguration(domainCode, data) {
    return this.post(`${this.baseURL}/delivery/domains/${domainCode}/share_configuration`, {
      body: JSON.stringify(data),
    });
  }

  requestAdditionalService(service) {
    return this.post(`${this.baseURL}/services/request`, {
      body: JSON.stringify({
        service,
      }),
    });
  }

  addMXRecord(data) {
    return this.post(`${this.baseURL}/system/mx`, {
      body: JSON.stringify(data),
    });
  }

  // PUT actions
  resetDKIM(code) {
    return this.put(`${this.baseURL}/domains/${code}/dkim/reset`);
  }

  verifyDKIM(code) {
    return this.put(`${this.baseURL}/domains/${code}/dkim`);
  }

  editEmailSignature(code, body) {
    return this.put(`${this.baseURL}/signatures/${code}`, {
      body: JSON.stringify(body),
    });
  }

  editDomain(code, body) {
    return this.put(`${this.baseURL}/domains/${code}`, {
      body: JSON.stringify(body),
    });
  }

  editArchiveDomain(code, body) {
    return this.put(`${this.baseURL}/archive/domains/${code}`, {
      body: JSON.stringify(body),
    });
  }

  editArchiveEmailAccount(code, domainCode, body) {
    return this.put(`${this.baseURL}/archive/domains/${domainCode}/email_accounts/${code}`, {
      body: JSON.stringify(body),
    });
  }

  editArchiveDomainStatus(code, status) {
    return this.put(`${this.baseURL}/archive/domains/${code}/status`, {
      body: JSON.stringify({ status }),
    });
  }

  editArchiveEmailAccountStatus(code, domainCode, status) {
    return this.put(`${this.baseURL}/archive/domains/${domainCode}/email_accounts/${code}/status`, {
      body: JSON.stringify({ status }),
    });
  }

  editAccount(code, body) {
    return this.put(`${this.baseURL}/users/${code}`, {
      body: JSON.stringify(body),
    });
  }

  editCustomer(code, body) {
    return this.put(`${this.baseURL}/users/customers/${code}`, {
      body: JSON.stringify(body),
    });
  }

  editAdmin(code, body) {
    return this.put(`${this.baseURL}/users/admins/${code}`, {
      body: JSON.stringify(body),
    });
  }

  editEmailAccount(codeDomain, codeEmail, body) {
    return this.put(`${this.baseURL}/domains/${codeDomain}/email_accounts/${codeEmail}`, {
      body: JSON.stringify(body),
    });
  }

  editAliasEmailAccount(codeDomain, codeAliasEmail, body) {
    return this.put(`${this.baseURL}/domains/${codeDomain}/alias_email_accounts/${codeAliasEmail}`, {
      body: JSON.stringify(body),
    });
  }

  editTeamMember(code, values) {
    return this.put(`${this.baseURL}/team_members/${code}`, {
      body: JSON.stringify(values),
    });
  }

  editManager(code, values) {
    return this.put(`${this.baseURL}/managers/${code}`, {
      body: JSON.stringify(values),
    });
  }

  previewAssignDeliveryResourceToManager(managerCode, resourceId) {
    return this.post(`${this.baseURL}/managers/${managerCode}/assign/delivery/preview`, {
      body: JSON.stringify({ id: resourceId }),
    });
  }

  assignDeliveryResourceToManager(managerCode, opts = {}) {
    return this.put(`${this.baseURL}/managers/${managerCode}/assign/delivery`, {
      body: JSON.stringify(opts),
    });
  }

  unassignDeliveryResourceToManager(managerCode, resourceId) {
    return this.delete(`${this.baseURL}/managers/${managerCode}/assign/delivery/${resourceId}`);
  }

  editDelegate(code, values) {
    return this.put(`${this.baseURL}/delegates/${code}`, {
      body: JSON.stringify(values),
    });
  }

  editCustomization(id, values) {
    return this.put(`${this.baseURL}/customizations/${id}`, {
      body: JSON.stringify(values),
    });
  }

  editMigrationOption(code, values) {
    return this.put(`${this.baseURL}/migration_options/${code}`, {
      body: JSON.stringify(values),
    });
  }

  editNews(id, values) {
    return this.put(`${this.baseURL}/news/${id}`, {
      body: JSON.stringify(values),
    });
  }

  editOrder(id, values) {
    return this.put(`${this.baseURL}/orders/${id}/update`, {
      body: JSON.stringify(values),
    });
  }

  changeCustomerStatus(code, status = "disabled") {
    return this.put(`${this.baseURL}/users/customers/${code}/status`, {
      body: JSON.stringify({ status }),
    });
  }

  changeTeamMemberStatus(code, status = "disabled") {
    return this.put(`${this.baseURL}/team_members/${code}/status`, {
      body: JSON.stringify({ status }),
    });
  }

  changeManagerStatus(code, status = "disabled") {
    return this.put(`${this.baseURL}/managers/${code}/status`, {
      body: JSON.stringify({ status }),
    });
  }

  changeDelegateStatus(code, status = "disabled") {
    return this.put(`${this.baseURL}/delegates/${code}/status`, {
      body: JSON.stringify({ status }),
    });
  }

  changeDomainStatus(code, status = "disabled") {
    return this.put(`${this.baseURL}/domains/${code}/status`, {
      body: JSON.stringify({ status }),
    });
  }

  changeEmailAccountStatus(domainCode, code, status = "disabled", bounceMsg) {
    return this.put(`${this.baseURL}/domains/${domainCode}/email_accounts/${code}/status`, {
      body: JSON.stringify({ status, bounce_msg: bounceMsg }),
    });
  }

  exportETlivePDF(body) {
    return this.put(`${this.baseURL}/etlive/export`, {
      body: JSON.stringify(body),
    });
  }

  ETliveWhere(body) {
    return this.post(`${this.baseURL}/etlive/where`, {
      body: JSON.stringify(body),
    });
  }

  changeDeliveryAccountStatus(id, newStatus) {
    return this.put(`${this.baseURL}/delivery/accounts/${id}/status`, { body: JSON.stringify({ status: newStatus }) });
  }

  cancelDeliveryPendingOrder(id) {
    return this.delete(`${this.baseURL}/delivery/accounts/${id}/pending`);
  }

  deleteMXRecord(id) {
    return this.delete(`${this.baseURL}/system/mx/${id}`);
  }

  updateDeliveryAccountInfo({
    id,
    description,
    bounceNotification,
    bounceNotificationToSender,
    domainCodes,
    relayIps,
    productId,
    volume,
    paymentMethod,
    num_messages,
    override_plan_num_messages,
    ignore_suppression_list,
  }) {
    const body = {};

    if (description) body.description = description;
    if (bounceNotification && !bounceNotificationToSender) body.bounce_notification = bounceNotification;
    if (domainCodes) body.domain_codes = domainCodes;
    body.bounce_notification_to_sender = bounceNotificationToSender;
    if (relayIps) body.relay_ips = relayIps;
    if (productId) body.product_id = productId;
    if (volume) body.volume = volume;
    if (paymentMethod) body.payment_method = paymentMethod;
    if (num_messages) body.num_messages = num_messages;
    body.override_plan_num_messages = override_plan_num_messages;

    body.ignore_suppression_list = ignore_suppression_list;

    return this.put(`${this.baseURL}/delivery/accounts/${id}/`, {
      body: JSON.stringify(body),
    });
  }

  previewManagerResources(id, domainCodes) {
    return this.put(`${this.baseURL}/delivery/accounts/${id}/preview-manager-assignment`, {
      body: JSON.stringify({
        domain_codes: domainCodes,
      }),
    });
  }

  updateSuppressionListItem({ email, customer_code, reason, accounts, active }) {
    return this.put(`${this.baseURL}/delivery/suppression_lists/edit`, {
      body: JSON.stringify({ email, customer_code, reason, accounts, active }),
    });
  }

  generateNewDeliveryAccountPassword(id) {
    return this.put(`${this.baseURL}/delivery/accounts/${id}/password`);
  }

  // DELETE actions
  deleteEmailSignature(code, identity) {
    return this.delete(`${this.baseURL}/signatures/${code}/${identity}`);
  }

  deleteDomain(code) {
    return this.delete(`${this.baseURL}/domains/${code}`);
  }

  disableDKIM(code) {
    return this.delete(`${this.baseURL}/domains/${code}/dkim`);
  }

  deleteEmailAccount(domainCode, emailCode) {
    return this.delete(`${this.baseURL}/domains/${domainCode}/email_accounts/${emailCode}`);
  }

  deleteAliasDomain(domainCode, code) {
    return this.delete(`${this.baseURL}/domains/${domainCode}/aliases/${code}`);
  }

  deleteAliasEmailAccount(domainCode, code) {
    return this.delete(`${this.baseURL}/domains/${domainCode}/alias_email_accounts/${code}`);
  }

  deleteEmailAlias() {}

  deleteTeamMember(code) {
    return this.delete(`${this.baseURL}/team_members/${code}`);
  }

  deleteManager(code) {
    return this.delete(`${this.baseURL}/managers/${code}`);
  }

  deleteDelegate(code) {
    return this.delete(`${this.baseURL}/delegates/${code}`);
  }

  deleteImport(code) {
    return this.delete(`${this.baseURL}/imports/${code}`);
  }

  deleteMigration(code) {
    return this.delete(`${this.baseURL}/migrations/${code}`);
  }

  deleteMigrationOption(code) {
    return this.delete(`${this.baseURL}/migration_options/${code}`);
  }

  deleteCustomization(code) {
    return this.delete(`${this.baseURL}/customizations/${code}`);
  }

  deleteCustomer(code) {
    return this.delete(`${this.baseURL}/users/customers/${code}`);
  }

  deleteAdmin(code) {
    return this.delete(`${this.baseURL}/users/admins/${code}`);
  }

  deleteNews(id) {
    return this.delete(`${this.baseURL}/news/${id}`);
  }

  deleteOrder(id) {
    return this.delete(`${this.baseURL}/orders/${id}`);
  }

  deleteArchiveEmail(code, domainCode) {
    return this.delete(`${this.baseURL}/archive/domains/${domainCode}/email_accounts/${code}`);
  }

  deleteArchiveDomain(domainCode) {
    return this.delete(`${this.baseURL}/archive/domains/${domainCode}`);
  }

  deleteDeliveryDomain(code) {
    return this.delete(`${this.baseURL}/delivery/domains/${code}`);
  }

  deleteDeliveryAccount(id, delete_after_billing) {
    return this.delete(`${this.baseURL}/delivery/accounts/${id}?delete_after_billing=${delete_after_billing}`);
  }

  restoreDeliveryAccount(id) {
    return this.post(`${this.baseURL}/delivery/accounts/${id}/restore`);
  }

  deleteSuppressionListItem({ email, customer_code }) {
    return this.post(`${this.baseURL}/delivery/suppression_lists/delete`, {
      body: JSON.stringify({ email, customer_code }),
    });
  }

  // Admin

  getOrders() {
    return this.get(`${this.baseURL}/orders`);
  }

  getPrices() {
    return this.get(`${this.baseURL}/orders/prices`);
  }

  getOrder(id) {
    return this.get(`${this.baseURL}/orders/${id}`);
  }

  refreshOrders() {
    return this.get(`${this.baseURL}/orders`);
  }

  editMigration(code, execution) {
    return this.put(`${this.baseURL}/migrations/${code}`, {
      body: JSON.stringify({ execution }),
    });
  }

  getLimitsStatus(code = null) {
    return this.get(`${this.baseURL}/internal/limits`, { code });
  }

  // etlive
  getAccesses(queryParams) {
    return this.get(`${this.baseURL}/etlive/accesses`, queryParams);
  }

  getMailins(queryParams) {
    return this.get(`${this.baseURL}/etlive/mailins`, queryParams);
  }

  getMailouts(queryParams) {
    return this.get(`${this.baseURL}/etlive/mailouts`, queryParams);
  }

  // enqueue DNS
  enqueueDNSCheck(code) {
    return this.put(`${this.baseURL}/domains/${code}/dns`);
  }

  // internal endpoints
  getArchiveEmailsForDomains(domainCodes) {
    return this.post(`${this.baseURL}/internal/domains_emails`, {
      body: JSON.stringify({ domain_codes: domainCodes }),
    });
  }

  SSOLogin(token) {
    return this.post(`${this.baseURL}/users/sso`, {
      body: JSON.stringify({
        sso_token: token,
      }),
    });
  }

  getCustomerTierPrices(customerCode) {
    return this.get(`${this.baseURL}/prices`, {
      customer_code: customerCode,
    });
  }

  requestTierUpgrade() {
    return this.post(`${this.baseURL}/prices/request_upgrade`, {
      body: JSON.stringify({}),
    });
  }

  verifyPromocode(code, cycle) {
    return this.get(`${this.baseURL}/promocodes/${code}`, {
      cycle,
    });
  }

  getMTMRequestes(filters = {}) {
    return this.get(`${this.baseURL}/mtm/restore_requests`, filters);
  }

  updateMTMRequest(id, params = {}) {
    return this.put(`${this.baseURL}/mtm/restore_requests/${id}`, {
      body: JSON.stringify(params),
    });
  }

  // tour endpoints

  sendTourData({ uuid, tourKey, stepKey, action }) {
    return this.post(`${this.baseURL}/tour/action`, {
      body: JSON.stringify({
        tour: tourKey,
        action,
        uuid,
        step: stepKey,
      }),
    });
  }
}
